import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { Cell, Rect, DisplayFounder, UndoAction } from '../types';
import { CustomFieldDefinition } from '../../../services/api';

interface TableContextValue {
  // Selection state
  activeCell: Cell | null;
  setActiveCell: (cell: Cell | null) => void;
  selectionRect: Rect | null;
  setSelectionRect: (rect: Rect | null) => void;
  anchorCell: Cell | null;
  setAnchorCell: (cell: Cell | null) => void;

  // Data
  founders: DisplayFounder[];
  customFields: CustomFieldDefinition[];
  colOrder: string[];

  // Config
  isTableLocked: boolean;
  setIsTableLocked: (locked: boolean) => void;

  // Editing trigger
  shouldStartEditing: { row: number; colKey: string; initialChar?: string } | null;
  setShouldStartEditing: (editing: { row: number; colKey: string; initialChar?: string } | null) => void;

  // Undo
  addUndoAction: (action: UndoAction) => void;
  performUndo: () => void;

  // Callbacks
  onFounderUpdate: (founderId: string, updates: Partial<DisplayFounder>) => void;
  onCustomFieldUpdate: (founderId: string, fieldId: string, value: string) => void;
}

const TableContext = createContext<TableContextValue | null>(null);

interface TableContextProviderProps {
  children: React.ReactNode;
  founders: DisplayFounder[];
  customFields: CustomFieldDefinition[];
  colOrder: string[];
  onFounderUpdate: (founderId: string, updates: Partial<DisplayFounder>) => void;
  onCustomFieldUpdate: (founderId: string, fieldId: string, value: string) => void;
}

export const TableContextProvider: React.FC<TableContextProviderProps> = ({
  children,
  founders,
  customFields,
  colOrder,
  onFounderUpdate,
  onCustomFieldUpdate,
}) => {
  // Selection state
  const [activeCell, setActiveCell] = useState<Cell | null>(null);
  const [selectionRect, setSelectionRect] = useState<Rect | null>(null);
  const [anchorCell, setAnchorCell] = useState<Cell | null>(null);

  // Config
  const [isTableLocked, setIsTableLocked] = useState(false);

  // Editing trigger
  const [shouldStartEditing, setShouldStartEditing] = useState<{
    row: number;
    colKey: string;
    initialChar?: string;
  } | null>(null);

  // Undo state
  const [undoHistory, setUndoHistory] = useState<UndoAction[]>([]);
  const MAX_UNDO_HISTORY = 50;

  const addUndoAction = useCallback((action: UndoAction) => {
    setUndoHistory((prev) => {
      const newHistory = [...prev, action];
      if (newHistory.length > MAX_UNDO_HISTORY) {
        return newHistory.slice(-MAX_UNDO_HISTORY);
      }
      return newHistory;
    });
  }, []);

  const performUndo = useCallback(() => {
    if (undoHistory.length === 0) return;

    const lastAction = undoHistory[undoHistory.length - 1];
    setUndoHistory((prev) => prev.slice(0, -1));

    if (lastAction.isCustomField) {
      onCustomFieldUpdate(lastAction.founderId, lastAction.colKey, lastAction.oldValue);
    } else {
      onFounderUpdate(lastAction.founderId, { [lastAction.colKey]: lastAction.oldValue });
    }
  }, [undoHistory, onFounderUpdate, onCustomFieldUpdate]);

  const value = useMemo<TableContextValue>(
    () => ({
      activeCell,
      setActiveCell,
      selectionRect,
      setSelectionRect,
      anchorCell,
      setAnchorCell,
      founders,
      customFields,
      colOrder,
      isTableLocked,
      setIsTableLocked,
      shouldStartEditing,
      setShouldStartEditing,
      addUndoAction,
      performUndo,
      onFounderUpdate,
      onCustomFieldUpdate,
    }),
    [
      activeCell,
      selectionRect,
      anchorCell,
      founders,
      customFields,
      colOrder,
      isTableLocked,
      shouldStartEditing,
      addUndoAction,
      performUndo,
      onFounderUpdate,
      onCustomFieldUpdate,
    ]
  );

  return <TableContext.Provider value={value}>{children}</TableContext.Provider>;
};

export const useTableContext = (): TableContextValue => {
  const context = useContext(TableContext);
  if (!context) {
    throw new Error('useTableContext must be used within a TableContextProvider');
  }
  return context;
};

export const useTableContextOrNull = (): TableContextValue | null => {
  return useContext(TableContext);
};

export default TableContext;
